import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Schools } from '../../core/models/schools';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, FormGroupDirective, NgForm, UntypedFormArray } from '@angular/forms';
import { Subscriptions } from '../../shared/subscriptions';
import { AuthenticationService } from '../../core/services/authentication.service';
import { SnackBarAlertsService } from '../../core/services/snackbar-alerts.service';
import { SchoolsService } from '../../core/services/schools.service';
import { ErrorStateMatcher } from '@angular/material/core';
import { Observable } from 'rxjs';
import { State, Country, StateCountryEventService } from '../../core/services/state-country-event.service';

/** Error when invalid control is dirty, touched, or submitted. This allows us to validate the form as the user is typing. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'app-school-contact-information',
  templateUrl: './school-contact-information.component.html',
  styleUrls: ['./school-contact-information.component.scss']
})
export class SchoolContactInformationComponent implements OnInit, OnDestroy {
  @Input() selectedSchoolId: string;

  schoolData: any;
  originalData: Schools;
  editModeOff: boolean;
  showDeleteIcon: boolean;
  checked: boolean;
  isSchoolUser: boolean;
  wrongUser: boolean;
  form: UntypedFormGroup;
  isServicerConfiguration = false;
  subscriptions = new Subscriptions();
  state: string;
  formCount: any;
  prefix: any;
  states$: Observable<State[]> = this.stateCountryService.states$;
  countries$: Observable<Country[]> = this.stateCountryService.usaOnly$;

  // being used for showing error while typing
  matcher = new MyErrorStateMatcher();

  constructor(
    private fb: UntypedFormBuilder,
    private authService: AuthenticationService,
    private alerts: SnackBarAlertsService,
    private schoolsService: SchoolsService,
    private stateCountryService: StateCountryEventService
  ) {
    this.isServicerConfiguration = this.authService.isServicerConfigUser();
    this.isSchoolUser = this.authService.isSchoolUser();
    if ( this.isSchoolUser === true || this.isServicerConfiguration === true) {
      this.wrongUser === false;
    }
    this.form = this.fb.group({
      contacts: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.schoolsService.getSchoolsById(this.selectedSchoolId).subscribe(data => {
      this.originalData = data;
      this.schoolData = data.contacts;
      this.schoolData.forEach(contact => {
        this.addContacts(contact);
      });
    });
  }
  /* main contacts */
  newContact(contact?: any): UntypedFormGroup {
    return this.fb.group({
      firstName: [contact ? contact.firstName : ''],
      middleName: [contact ? contact.middleName : ''],
      lastName: [contact ? contact.lastName : ''],
      title: [contact ? contact.title : ''],
      phoneNumbers: this.setPhones(contact?.phoneNumbers),
      emailAddresses: this.setEmails(contact?.emailAddresses),
      url: [contact ? contact.url : '']
    });
  }
  contacts(): UntypedFormArray {
    return this.form.get('contacts') as UntypedFormArray;
  }
  addContacts(contact?: any) {
    this.contacts().push(this.newContact(contact));
  }

  /* gotta set the phones data here upon first load */
  setPhones(contacts?: any) {
    const arr = new UntypedFormArray([]);
    contacts?.forEach(y => {
      arr.push(
        this.fb.group({
          phoneNumber: [contacts ? y.phoneNumber : ''],
          isPrimary: [contacts ? y.isPrimary : ''],
          isMobile: [contacts ? y.isMobile : '']
        })
      );
    });

    return arr;
  }
  /* delete phones */
  removePhone(conIndex: number, phIndex: number) {
    this.phone(conIndex).removeAt(phIndex);
  }
  /* adds new form control */
  addPhoneNumber(conIndex?: number) {
    this.phone(conIndex).push(this.newNumber());
  }
  phone(conIndex: number): UntypedFormArray {
    return this.contacts()
      .at(conIndex)
      .get('phoneNumbers') as UntypedFormArray;
  }
  /* creates new empty phone form */
  newNumber(): UntypedFormGroup {
    return this.fb.group({
      phoneNumber: '',
      isPrimary: '',
      isMobile: ''
    });
  }

  /* setting emails data */
  setEmails(contacts?: any) {
    const arr = new UntypedFormArray([]);
    contacts?.forEach(y => {
      arr.push(
        this.fb.group({
          emailAddress: [contacts ? y.emailAddress : ''],
          isPrimary: [contacts ? y.isPrimary : '']
        })
      );
    });
    return arr;
  }

  /* email form array */
  email(conIndex: number): UntypedFormArray {
    return this.contacts()
      .at(conIndex)
      .get('emailAddresses') as UntypedFormArray;
  }
  addEmail(conIndex: number) {
    this.email(conIndex).push(this.newEmail());
  }
  newEmail(): UntypedFormGroup {
    return this.fb.group({
      emailAddress: '',
      isPrimary: ''
    });
  }
  /* delete email */
  removeEmail(conIndex: number, e: number) {
    this.email(conIndex).removeAt(e);
  }

  // edit or cancel form
  editForm() {
    this.form.enable();
    this.editModeOff = true;
  }
  cancelEdit() {
    this.form.disable();
    this.editModeOff = false;
  }

  submit() {
    const originalSchoolObj = this.originalData;
    if (originalSchoolObj.contacts) {
      delete originalSchoolObj.contacts;
    }
    const contacts = this.form.controls.contacts.value;
    contacts.forEach(element => {
      element.phoneNumbers.forEach(ph => {
        Object.assign(ph.phoneNumber.startsWith('+1') ? ph.phoneNumber : `+1${ph.phoneNumber}`);
      });
    });
    /* merge this into the form object, allowing form value to overwrite original values */
    const mergedObj = { ...originalSchoolObj, contacts };
    console.log(mergedObj);

    this.subscriptions.add = this.schoolsService.updateSchool(this.selectedSchoolId, mergedObj).subscribe(
      data => {
        this.schoolData = data.contacts;
        this.form.patchValue(data.contacts);
        this.editModeOff = false;
        this.alerts.show('Update was successful!');
      },
      () => {
        this.alerts.show('Update has failed!');
      }
    );
  }

  // destroying our subscriptions
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
